import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken, removeToken, isTokenExpired } from '../utils/storage';
import { setBypassNavigationGuard } from '../hooks/useNavigationGuard';
import {
  MAINTENANCE_RELOAD_KEY,
  shouldReloadForMaintenance,
  markMaintenanceActive,
  clearMaintenanceActive,
} from '../utils/maintenanceReload';
import { installUnloadListeners, isUnloading } from '../utils/unloadState';
import { isHandsUnavailable } from './handsUnavailable';
import type { ApiError } from '../types/auth';
import type { ApiErrorResponse, ErrorPageState, SessionExpiredState } from '../types/errors';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;

// Callback to clear navigation guard before redirecting (FR-114)
let clearNavigationGuardCallback: (() => void) | null = null;

// Track the document unloading, so a request the BROWSER cancelled on navigation is not mistaken
// for an unreachable server further down (FR-154). See ../utils/unloadState for the full rationale.
if (typeof window !== 'undefined') {
  installUnloadListeners(window);
}

/**
 * Register a callback to clear the navigation guard before redirecting.
 * This prevents the "Leave site?" popup during session expiration or errors.
 */
export function registerNavigationGuardClear(callback: () => void): void {
  clearNavigationGuardCallback = callback;
}

/**
 * Unregister the navigation guard clear callback.
 */
export function unregisterNavigationGuardClear(): void {
  clearNavigationGuardCallback = null;
}

/**
 * Helper to check if current page is an auth/error page (should not redirect)
 */
function isPublicPage(): boolean {
  const path = window.location.pathname;
  return (
    path === '/' ||
    path.includes('/login') ||
    path.includes('/register') ||
    path.includes('/session-expired') ||
    path.includes('/error')
  );
}

/**
 * Helper to get the current URL for return navigation
 */
function getCurrentReturnUrl(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Clear navigation guard and redirect (FR-114, FR-171)
 * This ensures the "Leave site?" popup is not shown during error redirects.
 *
 * FR-171: Sets synchronous bypass flag BEFORE redirect to prevent beforeunload
 * from firing the "Leave site?" dialog. The async callback is still called for
 * cleanup but is no longer critical for bypassing the guard.
 */
function safeRedirect(url: string, state?: object): void {
  // FR-171: Set synchronous bypass flag FIRST (this takes effect immediately)
  setBypassNavigationGuard(true);

  // Call the async callback for cleanup (e.g., reset session state)
  // but this is no longer critical for bypassing the navigation guard
  if (clearNavigationGuardCallback) {
    clearNavigationGuardCallback();
  }

  // Use history state API to pass state, then redirect
  if (state) {
    // For state-based navigation, we need to use a different approach
    // since window.location doesn't support state.
    // Store state in sessionStorage temporarily
    sessionStorage.setItem('redirectState', JSON.stringify(state));
  }

  window.location.replace(url);
}

/**
 * Helper to redirect to session expired page (FR-110 to FR-114)
 * Uses a flag to prevent multiple redirects
 */
function redirectToSessionExpired(): void {
  if (isRedirecting) {
    return; // Already redirecting, don't trigger again
  }

  removeToken();

  if (!isPublicPage()) {
    isRedirecting = true;
    const state: SessionExpiredState = {
      returnUrl: getCurrentReturnUrl(),
    };
    safeRedirect('/session-expired', state);
  }
}

/**
 * Helper to redirect to error page (FR-115 to FR-127, FR-151 to FR-158)
 * Supports contextual messages for different error types.
 */
function redirectToErrorPage(
  referenceCode: string,
  options?: {
    correlationId?: string;
    message?: string;
    errorType?: ErrorPageState['errorType'];
    canRetry?: boolean;
  }
): void {
  if (isRedirecting) {
    return;
  }

  if (!isPublicPage()) {
    isRedirecting = true;
    const state: ErrorPageState = {
      referenceCode,
      correlationId: options?.correlationId,
      message: options?.message,
      errorType: options?.errorType,
      canRetry: options?.canRetry ?? true,
      returnUrl: getCurrentReturnUrl(),
    };
    safeRedirect('/error', state);
  }
}

/**
 * FR-151: Redirect to error page for 404 Not Found
 */
function redirectToNotFoundError(): void {
  redirectToErrorPage('NOT_FOUND', {
    message: 'The requested page or resource could not be found.',
    errorType: 'not-found',
    canRetry: false,
  });
}

/**
 * FR-152: Redirect to error page for 502 Bad Gateway
 */
function redirectToGatewayError(): void {
  redirectToErrorPage('GATEWAY_ERROR', {
    message: 'The server is temporarily unavailable. Please try again in a few moments.',
    errorType: 'gateway-error',
    canRetry: true,
  });
}

/**
 * FR-153: Redirect to error page for 504 Gateway Timeout
 */
function redirectToTimeoutError(): void {
  redirectToErrorPage('TIMEOUT', {
    message: 'The server took too long to respond. Please try again.',
    errorType: 'timeout',
    canRetry: true,
  });
}

/**
 * FR-154: Redirect to error page for network errors
 */
function redirectToNetworkError(): void {
  redirectToErrorPage('NETWORK_ERROR', {
    message: 'Unable to connect to the server. Please check your internet connection.',
    errorType: 'network-error',
    canRetry: true,
  });
}

/**
 * Reload the page so the browser hits nginx and receives the static
 * maintenance page (FR-132 to FR-136).
 *
 * Maintenance mode is served at the infrastructure level: while the flag file
 * exists, nginx returns 503 for every route and renders
 * client/public/maintenance.html (see PRODUCTION.md "Maintenance Mode").
 * Only in-flight API calls from an already-loaded SPA reach this code; a
 * reload hands the user over to that static page. Applies to ALL visitors,
 * authenticated or not.
 *
 * Guarded against reload loops: if the backend itself returns a transient 503
 * while nginx is NOT in maintenance mode, we reload at most once per 30s
 * (guard decision lives in ../utils/maintenanceReload for unit testing).
 *
 * `force` bypasses the once/30s throttle. It is used only after `confirmMaintenanceThenReload`
 * has proven (via a HEAD probe) that nginx really is in maintenance: such a
 * reload lands on the static 503 page where no SPA code runs, so it cannot loop
 * and the throttle would only get in the way (e.g. a login button pressed within
 * 30s of a prior reload would otherwise silently do nothing — FR-137).
 *
 * Exported so the router-level MaintenanceGuard can reuse the exact same guard
 * when it surfaces maintenance on an in-app navigation (FR-137, FR-138).
 */
export function reloadForMaintenance(force = false): void {
  const now = Date.now();
  if (!force && !shouldReloadForMaintenance(sessionStorage.getItem(MAINTENANCE_RELOAD_KEY), now)) {
    return;
  }
  sessionStorage.setItem(MAINTENANCE_RELOAD_KEY, String(now));
  // Suppress the "Leave site?" navigation guard, same as safeRedirect (FR-171)
  setBypassNavigationGuard(true);
  window.location.reload();
}

/**
 * Confirm maintenance with a lightweight HEAD probe, then surface the static
 * maintenance page (FR-137, FR-138).
 *
 * While the flag file exists nginx returns 503 for EVERY route, so a HEAD to the
 * origin root is an authoritative maintenance check that is independent of which
 * API call triggered it and of the current SPA route. This is what lets a 503
 * from a same-page action (e.g. pressing Login) reliably hand the visitor to the
 * maintenance page instead of silently doing nothing — the navigation-based
 * MaintenanceGuard can't fire because the route didn't change.
 *
 * - HEAD 503  → confirmed maintenance → force a reload (bypass the once/30s
 *   throttle; a confirmed-maintenance reload lands on the static page and can't loop).
 * - HEAD 2xx  → the 503 was a transient backend blip, NOT maintenance → clear the
 *   marker and do nothing, so the caller's normal error handling applies.
 *
 * Uses a bare `fetch` (not the API client) so it never re-enters these
 * interceptors or triggers the 404/500 error-page redirects.
 */
export function confirmMaintenanceThenReload(): void {
  fetch(`${window.location.origin}/`, { method: 'HEAD', cache: 'no-store' })
    .then((res) => {
      if (res.status === 503) {
        markMaintenanceActive();
        reloadForMaintenance(true);
      } else if (res.ok) {
        clearMaintenanceActive();
      }
    })
    .catch(() => {
      /* offline / probe failed — leave normal error handling in place */
    });
}

// Note: redirectToHome has been removed per FR-156.
// All error scenarios now redirect to specific error pages with explanations.
// Users are NEVER logged out without an explanation.

// Add auth token to requests with proactive expiration check (FR-048, FR-155)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // If we're already redirecting, cancel all new requests
    if (isRedirecting) {
      return Promise.reject(new Error('Redirecting due to error'));
    }

    const token = getToken();

    // Proactively check if token is expired before making the request (FR-155)
    if (token) {
      if (isTokenExpired(token)) {
        // Token is expired - redirect to Session Expired page (not home)
        redirectToSessionExpired();
        // Cancel the request by rejecting
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// NFR-008: Auto-retry on HTTP 429 (rate limited) with retryAfter delay, max 2 retries
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config;
    if (!config || error.response?.status !== 429) {
      return Promise.reject(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const retryCount = (config as any)._retryCount || 0;
    if (retryCount >= 2) {
      return Promise.reject(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config as any)._retryCount = retryCount + 1;

    // Read retryAfter from response body (seconds), default to 1 second
    let retryAfterMs = 1000;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = error.response?.data as any;
    if (responseData?.retryAfter && typeof responseData.retryAfter === 'number') {
      retryAfterMs = responseData.retryAfter * 1000;
    }

    await new Promise(resolve => setTimeout(resolve, retryAfterMs));
    return apiClient(config);
  }
);

// Handle auth errors and server errors (FR-045, FR-046, FR-047, FR-110 to FR-138)
apiClient.interceptors.response.use(
  (response) => {
    // A successful response means the site is up: clear any stale maintenance
    // marker so the app self-heals once maintenance ends (FR-137, FR-138).
    clearMaintenanceActive();
    return response;
  },
  (error: AxiosError<ApiError | ApiErrorResponse>) => {
    const status = error.response?.status;
    const token = getToken();

    // Check if this request opted out of redirect on 404
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const skipRedirectOn404 = (error.config as any)?.skipRedirectOn404;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const skipErrorRedirect = (error.config as any)?.skipErrorRedirect;

    // Handle 401 Unauthorized - session expired (FR-110 to FR-114)
    if (status === 401) {
      redirectToSessionExpired();
      return Promise.reject(error);
    }

    // Feature 011 (FR-030) — HTTP 423 Locked with code PASSWORD_CHANGE_REQUIRED
    // signals that the caller still owes a first-login password change. Bounce
    // the SPA to /change-password unless we're already there.
    if (status === 423 && token) {
      const responseData = error.response?.data as { code?: string } | undefined;
      if (responseData?.code === 'PASSWORD_CHANGE_REQUIRED') {
        if (!isRedirecting && window.location.pathname !== '/change-password') {
          isRedirecting = true;
          safeRedirect('/change-password');
        }
        return Promise.reject(error);
      }
    }

    // Handle 403 Forbidden - role gate denied the request (feature 009)
    // Redirect to /forbidden so the user sees the "Back to dashboard" page.
    // Unlike 401, this does NOT clear the token.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (status === 403 && token && !(error.config as any)?.skipForbiddenRedirect) {
      if (!isRedirecting && !isPublicPage() && window.location.pathname !== '/forbidden') {
        isRedirecting = true;
        safeRedirect('/forbidden');
      }
      return Promise.reject(error);
    }

    // Spec 030 (FR-005): a broken tournament-hand link answers 500 HANDS_UNAVAILABLE. The page that
    // asked shows its own retryable "could not be loaded" state, so this one 500 goes straight back
    // to it — redirecting would throw the learner off the table for a fault a retry can fix. Every
    // other 500 still goes to the error page below.
    if (status === 500 && isHandsUnavailable(error)) {
      return Promise.reject(error);
    }

    // Handle 500 Internal Server Error - redirect to error page (FR-115 to FR-127)
    if (status === 500 && !skipErrorRedirect) {
      const responseData = error.response?.data as ApiErrorResponse | undefined;
      const referenceCode = responseData?.error?.referenceCode || 'Unknown';
      const correlationId = error.response?.headers?.['x-correlation-id'];

      if (token) {
        redirectToErrorPage(referenceCode, {
          correlationId,
          errorType: 'server-error',
        });
      }
      return Promise.reject(error);
    }

    // Handle 503 Service Unavailable - maintenance mode (FR-132 to FR-138)
    // Surface the static maintenance page. Intentionally NOT gated on `token`
    // (maintenance affects anonymous visitors too) NOR on `skipErrorRedirect`:
    // maintenance is a global, planned state, so even a call that opts out of the
    // generic error redirect (e.g. login, background logging) MUST surface the
    // maintenance page rather than an error (FR-137).
    //
    // We mark the marker (so the next in-app navigation reacts too) and then
    // confirm-and-reload: a HEAD probe proves it's really maintenance and forces
    // a reload past the once/30s throttle. This is what fixes a 503 on a
    // same-page action (e.g. pressing Login) reliably showing the maintenance
    // page instead of silently doing nothing when a reload happened recently.
    if (status === 503) {
      markMaintenanceActive();
      confirmMaintenanceThenReload();
      return Promise.reject(error);
    }

    // Handle 405 Method Not Allowed - almost always maintenance (FR-137, FR-138).
    // The nginx maintenance page is a STATIC file, which only serves GET/HEAD, so
    // during maintenance a GET navigates to a 503 page but a POST/PUT/DELETE (e.g.
    // Login, or Keep/Don't Keep on the data-editing page) gets 405 instead of 503.
    // We therefore confirm with the HEAD probe (GET / returns 503 only during real
    // maintenance) and surface the maintenance page. If it's a genuine 405 (a real
    // method mismatch, not maintenance) the probe returns 2xx, nothing reloads, and
    // the caller's normal error handling applies. Not premarked active — the probe
    // decides — and not gated on `token`/`skipErrorRedirect`, same as 503.
    if (status === 405) {
      confirmMaintenanceThenReload();
      return Promise.reject(error);
    }

    // Handle 404 Not Found - redirect to error page with specific message (FR-151)
    if (status === 404 && !skipErrorRedirect && !skipRedirectOn404) {
      if (token) {
        redirectToNotFoundError();
      }
      return Promise.reject(error);
    }

    // Handle 502 Bad Gateway - nginx can't reach backend (FR-152)
    if (status === 502 && !skipErrorRedirect) {
      if (token) {
        redirectToGatewayError();
      }
      return Promise.reject(error);
    }

    // Handle 504 Gateway Timeout - backend too slow (FR-153)
    if (status === 504 && !skipErrorRedirect) {
      if (token) {
        redirectToTimeoutError();
      }
      return Promise.reject(error);
    }

    // Handle network errors (no response - server unreachable) (FR-154)
    //
    // Not while the document is unloading: the browser cancels every in-flight request when the
    // user navigates away, and those cancellations reach us indistinguishable from a dead server
    // (no error.response either way). Redirecting on them replaces the page the user asked for with
    // an error page, and the caller still gets the rejection, so nothing is swallowed.
    if (!error.response && !skipErrorRedirect) {
      if (token && !isUnloading()) {
        redirectToNetworkError();
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
